import { supabase } from './supabase';
import { usePatientsStore } from './stores/patients';
import { useUsersStore } from './stores/users';
import { useServicesStore } from './stores/services';
import { useDoctorsStore } from './stores/doctors';
import { usePrescriptionsStore } from './stores/prescriptions';
import { useBillsStore } from './stores/bills';
import { useMasterPatientsStore } from './stores/masterPatients';
import { useConsultationsStore } from './stores/consultations';
import { useMedicationTemplatesStore } from './stores/medicationTemplates';
import { useHospitalProfileStore } from './stores/hospitalProfile';
import { useAuthStore } from './auth';
import { performanceMonitor } from './utils/performance-monitor';

interface SubscriptionConfig {
  table: string;
  events: ('INSERT' | 'UPDATE' | 'DELETE')[];
  filters?: Record<string, any>;
  handler: (payload: any) => Promise<void>;
}

class SelectiveRealtimeManager {
  private subscriptions: Map<string, any> = new Map();
  private isInitialized = false;
  private currentUserRole: string | null = null;
  private currentPage: string | null = null;

  initialize() {
    if (this.isInitialized) {
      console.log('SelectiveRealtimeManager already initialized');
      return;
    }

    try {
      console.log('Initializing SelectiveRealtimeManager...');
      this.currentUserRole = useAuthStore.getState().user?.role || null;
      this.setupSelectiveSubscriptions();
      this.isInitialized = true;
      console.log('SelectiveRealtimeManager initialized successfully');
    } catch (error) {
      console.error('Error initializing SelectiveRealtimeManager:', error);
    }
  }

  updateContext(role?: string, page?: string) {
    const roleChanged = role && role !== this.currentUserRole;
    const pageChanged = page && page !== this.currentPage;
    
    if (roleChanged || pageChanged) {
      console.log('Context changed - updating subscriptions:', { role, page });
      this.currentUserRole = role || this.currentUserRole;
      this.currentPage = page || this.currentPage;
      this.reconfigureSubscriptions();
    }
  }

  private reconfigureSubscriptions() {
    // Clean up existing subscriptions
    this.cleanup();
    
    // Setup new subscriptions based on current context
    this.setupSelectiveSubscriptions();
  }

  private setupSelectiveSubscriptions() {
    const configs = this.getSubscriptionConfigs();
    
    configs.forEach(config => {
      this.setupSubscription(config);
    });
  }

  private getSubscriptionConfigs(): SubscriptionConfig[] {
    const configs: SubscriptionConfig[] = [];

    // Base subscriptions for all users
    // Services table realtime subscription - re-enabled with error handling
    configs.push({
      table: 'services',
      events: ['INSERT', 'UPDATE', 'DELETE'],
      handler: async (payload) => {
        console.log('Services real-time update:', payload);
        try {
          await useServicesStore.getState().fetchServices();
        } catch (error) {
          console.error('Error updating services from realtime:', error);
        }
      }
    });

    // Role-specific subscriptions
    switch (this.currentUserRole) {
      case 'admin':
        // Admin gets all subscriptions
        configs.push(
          {
            table: 'patients',
            events: ['INSERT', 'UPDATE', 'DELETE'],
            handler: async (payload) => {
              console.log('Patients real-time update (admin):', payload);
              await usePatientsStore.getState().fetchPatients();
              // Force UI update
              usePatientsStore.setState({ lastUpdated: Date.now() });
            }
          },
          {
            table: 'doctors',
            events: ['INSERT', 'UPDATE', 'DELETE'],
            handler: async (payload) => {
              console.log('Doctors real-time update (admin):', payload);
              await useDoctorsStore.getState().fetchDoctors();
            }
          },
          {
            table: 'profiles',
            events: ['INSERT', 'UPDATE', 'DELETE'],
            handler: async (payload) => {
              console.log('Users real-time update (admin):', payload);
              await useUsersStore.getState().fetchUsers();
            }
          },
          {
            table: 'prescriptions',
            events: ['INSERT', 'UPDATE', 'DELETE'],
            handler: async (payload) => {
              console.log('Prescriptions real-time update (admin):', payload);
              await usePrescriptionsStore.getState().fetchPrescriptions();
            }
          },
          {
            table: 'bills',
            events: ['INSERT', 'UPDATE', 'DELETE'],
            handler: async (payload) => {
              console.log('Bills real-time update (admin):', payload);
              await useBillsStore.getState().fetchBills();
            }
          },
          {
            table: 'master_patients',
            events: ['INSERT', 'UPDATE', 'DELETE'],
            handler: async (payload) => {
              console.log('Master patients real-time update (admin):', payload);
              await useMasterPatientsStore.getState().fetchMasterPatients();
            }
          },
          {
            table: 'consultations',
            events: ['INSERT', 'UPDATE', 'DELETE'],
            handler: async (payload) => {
              console.log('Consultations real-time update (admin):', payload);
              await useConsultationsStore.getState().fetchConsultations();
            }
          },
          {
            table: 'medication_templates',
            events: ['INSERT', 'UPDATE', 'DELETE'],
            handler: async (payload) => {
              console.log('Medication templates real-time update (admin):', payload);
              // Fetch templates for all doctors when admin updates
              const currentUser = useAuthStore.getState().user;
              if (currentUser?.id) {
                await useMedicationTemplatesStore.getState().fetchTemplates(currentUser.id);
              }
            }
          },
          {
            table: 'clinic_settings',
            events: ['INSERT', 'UPDATE', 'DELETE'],
            handler: async (payload) => {
              console.log('Clinic settings real-time update (admin):', payload);
              await useHospitalProfileStore.getState().fetchProfile();
            }
          }
        );
        break;

      case 'staff':
        // Staff gets patient, doctor, and related subscriptions
        configs.push(
          {
            table: 'patients',
            events: ['INSERT', 'UPDATE', 'DELETE'],
            handler: async (payload) => {
              console.log('Patients real-time update (staff):', payload);
              await usePatientsStore.getState().fetchPatients();
              // Force UI update
              usePatientsStore.setState({ lastUpdated: Date.now() });
            }
          },
          {
            table: 'doctors',
            events: ['INSERT', 'UPDATE', 'DELETE'],
            handler: async (payload) => {
              console.log('Doctors real-time update (staff):', payload);
              await useDoctorsStore.getState().fetchDoctors();
            }
          },
          {
            table: 'prescriptions',
            events: ['INSERT', 'UPDATE', 'DELETE'],
            handler: async (payload) => {
              console.log('Prescriptions real-time update (staff):', payload);
              await usePrescriptionsStore.getState().fetchPrescriptions();
            }
          },
          {
            table: 'bills',
            events: ['INSERT', 'UPDATE', 'DELETE'],
            handler: async (payload) => {
              console.log('Bills real-time update (staff):', payload);
              await useBillsStore.getState().fetchBills();
            }
          },
          {
            table: 'master_patients',
            events: ['INSERT', 'UPDATE', 'DELETE'],
            handler: async (payload) => {
              console.log('Master patients real-time update (staff):', payload);
              await useMasterPatientsStore.getState().fetchMasterPatients();
            }
          },
          {
            table: 'consultations',
            events: ['INSERT', 'UPDATE', 'DELETE'],
            handler: async (payload) => {
              console.log('Consultations real-time update (staff):', payload);
              await useConsultationsStore.getState().fetchConsultations();
            }
          },
          {
            table: 'medication_templates',
            events: ['INSERT', 'UPDATE', 'DELETE'],
            handler: async (payload) => {
              console.log('Medication templates real-time update (staff):', payload);
              const currentUser = useAuthStore.getState().user;
              if (currentUser?.id) {
                await useMedicationTemplatesStore.getState().fetchTemplates(currentUser.id);
              }
            }
          },
          {
            table: 'clinic_settings',
            events: ['INSERT', 'UPDATE', 'DELETE'],
            handler: async (payload) => {
              console.log('Clinic settings real-time update (staff):', payload);
              await useHospitalProfileStore.getState().fetchProfile();
            }
          }
        );
        break;

      case 'doctor':
        // Doctor gets filtered subscriptions based on page context
        if (this.currentPage === 'doctor-dashboard' || this.currentPage === 'e-prescription') {
          const currentDoctorId = useAuthStore.getState().user?.id;
          
          // Subscribe to patient UPDATE events (when doctor is assigned)
          // Listen to ALL UPDATE events and filter in handler to catch doctor assignments
          configs.push(
            {
              table: 'patients',
              events: ['UPDATE'],
              // No filter - we'll check in handler to catch all doctor assignments
              handler: async (payload) => {
                console.log('Patients real-time update (doctor - UPDATE):', payload);
                const newData = payload.new;
                const oldData = payload.old;
                
                // Check if doctor_id was just assigned/changed to this doctor
                const newDoctorId = newData?.doctor_id;
                const oldDoctorId = oldData?.doctor_id;
                
                // If patient was just assigned to this doctor (new doctor_id matches, or changed to this doctor)
                if (newDoctorId === currentDoctorId && oldDoctorId !== currentDoctorId) {
                  console.log('Patient assigned to this doctor - refreshing...');
                  await usePatientsStore.getState().fetchPatients();
                  // Force UI update
                  usePatientsStore.setState({ lastUpdated: Date.now() });
                }
                // Also refresh if patient is already assigned to this doctor and any other field changed
                // (like vitals, status, etc.) - this handles vitals updates
                else if (newDoctorId === currentDoctorId) {
                  console.log('Patient assigned to this doctor - any field updated, refreshing...');
                  await usePatientsStore.getState().fetchPatients();
                  // Force UI update
                  usePatientsStore.setState({ lastUpdated: Date.now() });
                }
              }
            },
            // Subscribe to INSERT events to catch new patients that might be assigned immediately
            // Note: Most new patients won't have doctor_id yet, but we refresh to be safe
            // The doctor dashboard filters by doctor_id, so it will only show relevant patients
            {
              table: 'patients',
              events: ['INSERT'],
              handler: async (payload) => {
                console.log('Patients real-time update (doctor - INSERT):', payload);
                const newPatient = payload.new;
                
                // Only refresh if the new patient is assigned to this doctor
                // (though this is rare - usually assignment happens via UPDATE)
                if (newPatient?.doctor_id === currentDoctorId) {
                  console.log('New patient assigned to this doctor - refreshing...');
                  await usePatientsStore.getState().fetchPatients();
                  // Force UI update
                  usePatientsStore.setState({ lastUpdated: Date.now() });
                }
                // Otherwise, don't refresh - new unassigned patients don't affect doctor dashboard
              }
            },
            // Subscribe to DELETE events for patients assigned to this doctor
            {
              table: 'patients',
              events: ['DELETE'],
              filters: {
                doctor_id: currentDoctorId
              },
              handler: async (payload) => {
                console.log('Patients real-time update (doctor - DELETE):', payload);
                await usePatientsStore.getState().fetchPatients();
                // Force UI update
                usePatientsStore.setState({ lastUpdated: Date.now() });
              }
            },
            {
              table: 'prescriptions',
              events: ['INSERT', 'UPDATE', 'DELETE'],
              filters: {
                // Only subscribe to prescriptions by this doctor
                doctor_id: useAuthStore.getState().user?.id
              },
              handler: async (payload) => {
                console.log('Prescriptions real-time update (doctor):', payload);
                await usePrescriptionsStore.getState().fetchPrescriptions();
              }
            },
            {
              table: 'master_patients',
              events: ['INSERT', 'UPDATE', 'DELETE'],
              handler: async (payload) => {
                console.log('Master patients real-time update (doctor):', payload);
                await useMasterPatientsStore.getState().fetchMasterPatients();
              }
            },
            {
              table: 'consultations',
              events: ['INSERT', 'UPDATE', 'DELETE'],
              filters: {
                // Only subscribe to consultations by this doctor
                doctor_id: useAuthStore.getState().user?.id
              },
              handler: async (payload) => {
                console.log('Consultations real-time update (doctor):', payload);
                await useConsultationsStore.getState().fetchConsultations();
              }
            },
            {
              table: 'medication_templates',
              events: ['INSERT', 'UPDATE', 'DELETE'],
              filters: {
                // Only subscribe to templates by this doctor
                doctor_id: useAuthStore.getState().user?.id
              },
              handler: async (payload) => {
                console.log('Medication templates real-time update (doctor):', payload);
                const currentUser = useAuthStore.getState().user;
                if (currentUser?.id) {
                  await useMedicationTemplatesStore.getState().fetchTemplates(currentUser.id);
                }
              }
            },
            {
              table: 'clinic_settings',
              events: ['INSERT', 'UPDATE', 'DELETE'],
              handler: async (payload) => {
                console.log('Clinic settings real-time update (doctor):', payload);
                await useHospitalProfileStore.getState().fetchProfile();
              }
            }
          );
        }
        break;

      default:
        // No additional subscriptions for other roles
        break;
    }

    // Page-specific subscriptions
    if (this.currentPage === 'queue') {
      // Queue page needs real-time patient status updates
      configs.push({
        table: 'patients',
        events: ['UPDATE'],
        handler: async (payload) => {
          console.log('Patient status update (queue):', payload);
          // Force a complete refresh of patients data
          await usePatientsStore.getState().fetchPatients();
          // Also trigger a re-render by updating the store timestamp
          usePatientsStore.setState({ lastUpdated: Date.now() });
        }
      });
    }

    return configs;
  }

  private setupSubscription(config: SubscriptionConfig) {
    try {
      const channelName = `${config.table}_${this.currentUserRole}_${this.currentPage || 'general'}`;
      
             // Check if we already have this subscription to avoid duplicates
             if (this.subscriptions.has(channelName)) {
               return;
             }
      
      let channel = supabase.channel(channelName);

      // Add event listeners based on config
      config.events.forEach(event => {
        channel = channel.on('postgres_changes', 
          { 
            event: event.toLowerCase() as any, 
            schema: 'public', 
            table: config.table,
            filter: config.filters ? this.buildFilterString(config.filters) : undefined
          }, 
                      async (payload) => {
              const startTime = Date.now();
              const dataSize = JSON.stringify(payload).length;
              
              console.log(`${config.table} ${event} real-time update:`, payload);
              try {
                await config.handler(payload);
                const responseTime = Date.now() - startTime;
                performanceMonitor.trackSubscription(`${config.table}_${event}`, dataSize, responseTime);
              } catch (error) {
                console.error(`Error handling ${config.table} ${event}:`, error);
                const responseTime = Date.now() - startTime;
                performanceMonitor.trackSubscription(`${config.table}_${event}_error`, dataSize, responseTime);
              }
            }
        );
      });

      channel.subscribe((status) => {
        console.log(`${channelName} subscription status:`, status);
        
        // Handle connection errors gracefully
         if (status === 'CHANNEL_ERROR') {
           console.error(`Channel error for ${channelName}. This may be due to missing realtime permissions or table not found.`);
           // Only retry once to avoid infinite loops
           if (!this.subscriptions.has(channelName + '_retried')) {
             this.subscriptions.set(channelName + '_retried', true);
             setTimeout(() => {
               if (this.subscriptions.has(channelName)) {
                 this.setupSubscription(config);
               }
             }, 5000);
           }
         } else if (status === 'TIMED_OUT') {
          console.warn(`Channel timeout for ${channelName}. Retrying...`);
          // Retry subscription
          setTimeout(() => {
            if (this.subscriptions.has(channelName)) {
              this.setupSubscription(config);
            }
          }, 2000);
        }
      });

      this.subscriptions.set(channelName, channel);
    } catch (error) {
      console.error(`Error setting up ${config.table} subscription:`, error);
    }
  }

  private buildFilterString(filters: Record<string, any>): string {
    // Convert filters object to Supabase filter string
    const filterParts = Object.entries(filters).map(([key, value]) => {
      if (Array.isArray(value)) {
        return `${key}=in.(${value.join(',')})`;
      }
      return `${key}=eq.${value}`;
    });
    return filterParts.join(',');
  }

  cleanup() {
    console.log('Cleaning up SelectiveRealtimeManager...');
    this.subscriptions.forEach((channel, key) => {
      console.log(`Cleaning up ${key} subscription`);
      try {
        supabase.removeChannel(channel);
      } catch (error) {
        console.error(`Error cleaning up ${key} subscription:`, error);
      }
    });
    this.subscriptions.clear();
    this.isInitialized = false;
    console.log('SelectiveRealtimeManager cleanup completed');
  }

  // Get subscription status for debugging
  getSubscriptionStatus() {
    return {
      isInitialized: this.isInitialized,
      currentUserRole: this.currentUserRole,
      currentPage: this.currentPage,
      activeSubscriptions: Array.from(this.subscriptions.keys()),
      subscriptionCount: this.subscriptions.size
    };
  }
}

export const selectiveRealtimeManager = new SelectiveRealtimeManager(); 